import { useState, useEffect } from 'react';
import { Compass, ExternalLink, Loader2 } from 'lucide-react';
import { getMethodMetrics } from '@/services/api';

// Owner view of the K.A.N.R.I. teaser: how many people opened the
// method page and how many of them asked for the method.
const RANGES = [7, 30, 90];

export const MethodMetricsCard = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMethodMetrics(days)
      .then((d) => { if (alive) setData(d); })
      .catch(() => { if (alive) setData(null); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [days]);

  const peak = Math.max(1, ...((data?.per_day || []).map((d) => d.opens)));

  return (
    <div className="rounded-xl border border-muted/60 bg-card p-4" data-testid="method-metrics-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Compass className="h-4 w-4 text-primary" />
          <div>
            <h3 className="text-sm font-semibold text-foreground">KANRI method teaser</h3>
            <p className="text-xs text-muted-foreground">Page opens vs requests</p>
          </div>
        </div>
        <div className="flex items-center gap-1">
          {RANGES.map((r) => (
            <button
              key={r}
              onClick={() => setDays(r)}
              className={`rounded-md px-2 py-1 text-xs transition-colors cursor-pointer ${
                days === r ? 'bg-primary/10 text-primary font-semibold' : 'text-muted-foreground hover:text-foreground'
              }`}
              data-testid={`method-metrics-range-${r}`}
            >
              {r}d
            </button>
          ))}
          <a href="/method" target="_blank" rel="noreferrer"
             className="ml-1 text-muted-foreground hover:text-foreground" title="Open the method page"
             data-testid="method-metrics-open-page">
            <ExternalLink className="h-3.5 w-3.5" />
          </a>
        </div>
      </div>

      {loading ? (
        <div className="flex h-24 items-center justify-center text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
        </div>
      ) : !data ? (
        <p className="mt-4 text-sm text-muted-foreground">Could not load the method metrics.</p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-3 gap-3">
            {[
              { label: 'Opened', value: data.opens, sub: `${data.opens_all_time} all time`, testid: 'opens' },
              { label: 'Requested', value: data.requests, sub: `${data.requests_all_time} all time`, testid: 'requests' },
              { label: 'Conversion', value: `${data.conversion_percent}%`, sub: 'request per open', testid: 'conversion' },
            ].map((s) => (
              <div key={s.label} className="rounded-lg border border-muted/50 p-3" data-testid={`method-metrics-${s.testid}`}>
                <p className="text-xl font-bold tabular-nums text-foreground">{s.value}</p>
                <p className="text-xs text-foreground/70">{s.label}</p>
                <p className="text-[11px] text-muted-foreground">{s.sub}</p>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-end gap-[2px] h-14" data-testid="method-metrics-chart">
            {data.per_day.map((d) => (
              <div key={d.date} className="flex-1 flex flex-col justify-end gap-[1px]"
                   title={`${d.date} · ${d.opens} opened · ${d.requests} requested`}>
                {d.requests > 0 && <div className="w-full rounded-sm bg-primary" style={{ height: 6 }} />}
                <div className="w-full rounded-sm bg-primary/25"
                     style={{ height: `${Math.max(2, (d.opens / peak) * 40)}px` }} />
              </div>
            ))}
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground">
            Light bars = opens per day, solid mark = a request that day. Opens are counted once per visitor session.
          </p>

          {data.recent_requests.length > 0 && (
            <div className="mt-4 border-t border-muted/50 pt-3">
              <p className="text-xs font-semibold text-foreground/80">Latest requests</p>
              <ul className="mt-1.5 space-y-1" data-testid="method-metrics-recent">
                {data.recent_requests.map((r) => (
                  <li key={r.contact_id} className="flex items-center justify-between gap-3 text-xs">
                    <span className="truncate text-foreground/80">
                      {r.name}{r.company ? ` · ${r.company}` : ''}
                    </span>
                    <span className="shrink-0 text-muted-foreground tabular-nums">{(r.created_at || '').slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default MethodMetricsCard;
